import React, { useState, useContext, useEffect } from 'react';
import { ChatContext } from './ChatContext';
import { UserContext } from './App';
import { FaTimes, FaUser, FaCoins, FaPaperPlane, FaExclamationTriangle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './TipModal.css';

const TipModal = ({ onClose, initialRecipient = '' }) => {
  const { socket } = useContext(ChatContext);
  const { user, activeBalance } = useContext(UserContext);
  
  const [recipient, setRecipient] = useState(initialRecipient);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const currentBalance = user 
    ? (activeBalance === 'promo' ? Number(user.promoBalance || 0) : Number(user.balance || 0))
    : 0;

  useEffect(() => {
    if (initialRecipient) {
      setRecipient(initialRecipient);
    }
  }, [initialRecipient]);

  const handleSendTip = () => {
    setError('');
    const amountNum = parseFloat(amount);

    if (!recipient.trim()) return setError('Please enter a recipient username.');
    if (isNaN(amountNum) || amountNum < 0.5) return setError('Minimum tip amount is $0.50.');
    if (amountNum > currentBalance) return setError('Insufficient balance.');
    if (recipient.trim().toLowerCase() === user?.username?.toLowerCase()) {
      return setError('You cannot tip yourself.');
    }

    setSending(true);
    
    // Send tip through socket
    if (socket) {
      socket.emit('chatMessage', {
        text: `/tip ${recipient.trim()} ${amountNum.toFixed(2)}`,
        user: user.username,
        id: Date.now()
      });
      
      // We assume success for the UI state transition, 
      // actual errors will be caught by the chatError listener in ChatSidebar/Context
      setTimeout(() => {
        setSending(false);
        onClose();
      }, 500);
    } else {
      setError('Socket connection lost. Please try again.');
      setSending(false);
    }
  };

  const addAmount = (val) => {
    const current = parseFloat(amount) || 0;
    setAmount((current + val).toFixed(2));
  };

  return (
    <div className="tip-modal-overlay" onClick={onClose}>
      <motion.div 
        className="tip-modal-card"
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <div className="tip-modal-header">
          <h2>Send a Tip</h2>
          <button className="tip-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="tip-modal-body">
          {error && (
            <motion.div 
              className="tip-error-banner"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FaExclamationTriangle size={14} />
              {error}
            </motion.div>
          )}

          <div className="tip-input-group">
            <label>Recipient Username</label>
            <div className="tip-field-wrapper">
              <FaUser className="tip-field-icon" />
              <input 
                type="text" 
                className="tip-input"
                placeholder="Who are you tipping?"
                value={recipient}
                onChange={e => setRecipient(e.target.value)}
              />
            </div>
          </div>

          <div className="tip-input-group">
            <label>Amount (USD)</label>
            <div className="tip-field-wrapper">
              <FaCoins className="tip-field-icon" />
              <input 
                type="number" 
                step="0.01"
                className="tip-input"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            </div>
            
            <div className="tip-quick-amounts">
              <button className="tip-pill" onClick={() => addAmount(1)}>+$1</button>
              <button className="tip-pill" onClick={() => addAmount(5)}>+$5</button>
              <button className="tip-pill" onClick={() => addAmount(10)}>+$10</button>
              <button className="tip-pill" onClick={() => addAmount(25)}>+$25</button>
            </div>

            <div className="tip-balance-info">
              <span className="tip-balance-label">Available Balance</span>
              <span className="tip-balance-value">
                <span>$</span>{currentBalance.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="tip-footer">
            <button 
              className="tip-submit-btn" 
              onClick={handleSendTip}
              disabled={sending || !recipient || !amount || parseFloat(amount) <= 0}
            >
              <FaPaperPlane />
              {sending ? 'Sending...' : 'Confirm Tip'}
            </button>
            
            <p className="tip-disclaimer">
              Tips are instant and cannot be reversed.<br />
              The recipient must wager the tip amount before withdrawing.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TipModal;
